import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Phone, Mail, MapPin, Clock, Send, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PHONE_NUMBER = "306-262-5995";
const EMAIL = "306LOCKSMITH@GMAIL.COM";

const ContactSection = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    service_type: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData((prev) => ({ ...prev, service_type: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      toast.success("Quote request submitted! We'll contact you shortly.");
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({
          name: "",
          phone: "",
          email: "",
          service_type: "",
          message: "",
        });
      }, 3000);
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to submit. Please call us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      className="py-20 md:py-32 bg-slate-900"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div>
            <span className="text-sm uppercase tracking-[0.2em] font-semibold text-amber-400 mb-4 block">
              Contact Us
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white mb-6">
              Need a Locksmith?
              <br />
              <span className="text-amber-400">We're Here 24/7</span>
            </h2>
            <p className="text-slate-400 text-lg mb-10">
              Whether it's an emergency lockout or a scheduled service, reach out to us anytime. We're ready to help!
            </p>

            {/* Contact Details */}
            <div className="space-y-6">
              <ContactItem
                icon={<Phone className="w-6 h-6" />}
                label="Call Us (24/7)"
                value={PHONE_NUMBER}
                href={`tel:${PHONE_NUMBER.replace(/-/g, "")}`}
                highlight
              />
              <ContactItem
                icon={<Mail className="w-6 h-6" />}
                label="Email Us"
                value={EMAIL}
                href={`mailto:${EMAIL}`}
              />
              <ContactItem
                icon={<MapPin className="w-6 h-6" />}
                label="Service Area"
                value="Saskatoon & Surrounding Areas"
              />
              <ContactItem
                icon={<Clock className="w-6 h-6" />}
                label="Hours"
                value="24 Hours / 7 Days a Week"
              />
            </div>

            {/* Quick Call CTA */}
            <div className="mt-10 p-6 bg-amber-500 rounded-sm">
              <p className="text-slate-900 font-semibold mb-2">
                Emergency? Don't wait!
              </p>
              <a
                href={`tel:${PHONE_NUMBER.replace(/-/g, "")}`}
                className="inline-flex items-center gap-2 text-slate-900 font-heading text-2xl hover:text-blue-900 transition-colors"
                data-testid="emergency-call-link"
              >
                <Phone className="w-6 h-6" />
                Call {PHONE_NUMBER} Now
              </a>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-sm p-8" data-testid="contact-form-container">
            {isSubmitted ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-12">
                <CheckCircle className="w-16 h-16 text-green-500 mb-4" />
                <h3 className="font-heading text-2xl text-slate-900 mb-2">
                  Request Received!
                </h3>
                <p className="text-slate-600">
                  We'll get back to you within the hour.
                </p>
              </div>
            ) : (
              <>
                <h3 className="font-heading text-2xl text-slate-900 mb-2">
                  Request a Free Quote
                </h3>
                <p className="text-slate-600 mb-6">
                  Fill out the form below and we'll respond promptly.
                </p>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <Label htmlFor="name" className="text-slate-700">
                      Your Name *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="John Smith"
                      required
                      className="mt-1 rounded-sm border-slate-300"
                      data-testid="contact-name-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone" className="text-slate-700">
                      Phone Number *
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="306-555-1234"
                      required
                      className="mt-1 rounded-sm border-slate-300"
                      data-testid="contact-phone-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-slate-700">
                      Email (Optional)
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@example.com"
                      className="mt-1 rounded-sm border-slate-300"
                      data-testid="contact-email-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="service" className="text-slate-700">
                      Service Type *
                    </Label>
                    <Select
                      value={formData.service_type}
                      onValueChange={handleServiceChange}
                      required
                    >
                      <SelectTrigger
                        className="mt-1 rounded-sm border-slate-300"
                        data-testid="contact-service-select"
                      >
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="residential">
                          Residential Locksmith
                        </SelectItem>
                        <SelectItem value="commercial">
                          Commercial Locksmith
                        </SelectItem>
                        <SelectItem value="automotive">
                          Automotive Locksmith
                        </SelectItem>
                        <SelectItem value="emergency">
                          Emergency Lockout
                        </SelectItem>
                        <SelectItem value="other">
                          Other Service
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-slate-700">
                      Message *
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about your situation..."
                      required
                      rows={4}
                      className="mt-1 rounded-sm border-slate-300 resize-none"
                      data-testid="contact-message-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-blue-900 hover:bg-blue-800 text-white font-semibold py-6 rounded-sm"
                    data-testid="contact-submit-button"
                  >
                    {isSubmitting ? (
                      "Sending..."
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Submit Request
                      </>
                    )}
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const ContactItem = ({ icon, label, value, href, highlight }) => (
  <div className="flex items-start gap-4">
    <div
      className={`w-12 h-12 rounded-sm flex items-center justify-center flex-shrink-0 ${
        highlight ? "bg-amber-500 text-slate-900" : "bg-slate-800 text-amber-400"
      }`}
    >
      {icon}
    </div>
    <div>
      <p className="text-slate-400 text-sm">{label}</p>
      {href ? (
        <a
          href={href}
          className={`font-semibold text-lg hover:text-amber-400 transition-colors ${
            highlight ? "text-amber-400" : "text-white"
          }`}
          data-testid={`contact-${label.toLowerCase().replace(/\s/g, "-")}`}
        >
          {value}
        </a>
      ) : (
        <p className="text-white font-semibold text-lg">{value}</p>
      )}
    </div>
  </div>
);

export default ContactSection;
